import {reportException} from '@packages/utilities/reporter';

// Pure, DOM-API-only walker that converts an `Element` subtree into a
// markdown-flavored string suitable for sending to an LLM as page context.
//
// Lives in `usage:universal` so both frontend (real DOM) and backend (jsdom)
// can share the same conversion logic. Output format is the source of truth
// for AEO/SEO prompts — keeping the FE iframe path and the server audit path
// in lockstep avoids prompt drift between sources.
//
// Implementation note: this file MUST stay free of environment-specific
// globals. In particular, do not reference `Node.TEXT_NODE` directly (it is
// not a Node.js global). Use the spec literal `3` instead.

const STRIPPED_TAGS = new Set(['IFRAME', 'SCRIPT', 'STYLE', 'SVG']);

const CONVERT_TAGS = new Set([
  'AUDIO',
  'VIDEO',
  'CANVAS',
  'OBJECT',
  'EMBED',
  'AREA',
  'TRACK',
]);

const DECORATIVE_CHARS = new Set([
  '→',
  '↗',
  '►',
  '↓',
  '←',
  '↑',
  '↘',
  '↙',
  '↖',
  '↕',
  '↔',
  '▶',
  '▷',
  '◀',
  '◁',
  '▲',
  '▼',
  '◆',
  '◇',
  '●',
  '○',
  '■',
  '□',
  '★',
  '☆',
  '✓',
  '✗',
  '✕',
  '✖',
  '✚',
  '✜',
  '⚡',
  '⚠',
  '⚪',
  '⚫',
  '•',
  '·',
  '‣',
  '⁌',
  '⁍',
  '※',
  '‖',
  '¦',
  '§',
  '¶',
  '‡',
  '†',
  '⟩',
  '⟨',
  '〉',
  '〈',
  '》',
  '《',
  '〉',
  '〈',
  '⋙',
  '⋘',
  '≫',
  '≪',
]);

const MAX_RECURSION_DEPTH = 100;

const INVISIBLE_CLASS = 'w-condition-invisible';

const SEMANTIC_CONTAINERS = new Set([
  'article',
  'section',
  'nav',
  'aside',
  'header',
  'footer',
  'main',
]);

// DOM Node.TEXT_NODE constant — `3` per the DOM spec. Hard-coded so this
// module does not depend on the `Node` global being present (it is not in
// Node.js without jsdom installed globally).
const TEXT_NODE_TYPE = 3;

const reportIssue = (error: Error, description?: string): void => {
  reportException(
    error,
    {
      description: `[SEO][htmlToSemanticMarkdown] ${description ?? error.message}`,
    },
    {
      severity: 'warning',
    }
  );
};

export const toSemanticMarkdownFromElement = (rootElement: Element): string => {
  try {
    const contentParts: string[] = [];
    processElementRecursively(rootElement, contentParts);

    return contentParts
      .filter(Boolean)
      .map((part, index) => {
        const needsLineBreak =
          part.startsWith('#') ||
          part.startsWith('<') ||
          part.startsWith('IMAGE:') ||
          part.startsWith('VIDEO:') ||
          part.startsWith('AUDIO:') ||
          part.startsWith('EMBED:') ||
          part.startsWith('OBJECT:');

        if (index === 0) return part;
        return needsLineBreak ? '\n' + part : ' ' + part;
      })
      .join('')
      .replace(/\s+/g, ' ')
      .replace(/\n /g, '\n')
      .trim();
  } catch (error) {
    reportIssue(error as Error, 'Error in toSemanticMarkdownFromElement');
    try {
      return rootElement.textContent?.trim() || '';
    } catch (fallbackError) {
      reportIssue(fallbackError as Error, 'Error in textContent fallback');
      return '';
    }
  }
};

const processElementRecursively = (
  element: Element,
  contentParts: string[],
  depth: number = 0
): void => {
  try {
    if (depth > MAX_RECURSION_DEPTH) {
      reportIssue(
        new Error(`Maximum recursion depth (${MAX_RECURSION_DEPTH}) exceeded`),
        'Depth limit reached in processElementRecursively'
      );
      return;
    }

    if (!element || !element.tagName) {
      return;
    }

    const tagName = element.tagName.toLowerCase();

    try {
      if (element.classList && element.classList.contains(INVISIBLE_CLASS)) {
        return;
      }
    } catch (error) {
      reportIssue(error as Error, 'Error checking invisible class on element');
    }

    if (tagName === 'noscript') {
      try {
        const children = Array.from(element.children);
        for (const child of children) {
          processElementRecursively(child as Element, contentParts, depth + 1);
        }
      } catch (error) {
        reportIssue(error as Error, 'Error processing noscript children');
      }
      return;
    }

    if (SEMANTIC_CONTAINERS.has(tagName)) {
      try {
        contentParts.push(`<${tagName}>`);

        const children = Array.from(element.children);
        for (const child of children) {
          processElementRecursively(child as Element, contentParts, depth + 1);
        }

        const directText = getDirectTextContent(element);
        if (directText) {
          contentParts.push(directText);
        }

        contentParts.push(`</${tagName}>`);
      } catch (error) {
        reportIssue(
          error as Error,
          `Error processing semantic container: ${tagName}`
        );
      }
      return;
    }

    try {
      const content = extractSemanticContent(element);
      if (content) {
        contentParts.push(content);
      }
    } catch (error) {
      reportIssue(error as Error, `Error extracting content from ${tagName}`);
    }

    try {
      const children = Array.from(element.children);
      for (const child of children) {
        processElementRecursively(child as Element, contentParts, depth + 1);
      }
    } catch (error) {
      reportIssue(error as Error, `Error processing children of ${tagName}`);
    }
  } catch (error) {
    reportIssue(error as Error, 'Error in processElementRecursively');
  }
};

const extractSemanticContent = (node: Element): string | null => {
  try {
    if (!node || !node.tagName) {
      return null;
    }

    try {
      if (
        STRIPPED_TAGS.has(node.tagName) ||
        node.id === 'lightbox-mountpoint'
      ) {
        return null;
      }
    } catch (error) {
      reportIssue(error as Error, 'Error checking stripped tags or lightbox');
      return null;
    }

    try {
      if (node.classList && node.classList.contains(INVISIBLE_CLASS)) {
        return null;
      }
    } catch (error) {
      reportIssue(error as Error, 'Error checking invisible class on node');
    }

    const tagName = node.tagName.toLowerCase();

    if (/^h[1-6]$/.test(tagName)) {
      try {
        const level = parseInt(tagName.charAt(1), 10);
        const headingText = cleanText(node.textContent || '');
        if (headingText) {
          return `${'#'.repeat(level)} ${headingText}`;
        }
      } catch (error) {
        reportIssue(error as Error, `Error processing heading: ${tagName}`);
      }
      return null;
    }

    if (tagName === 'img') {
      try {
        const alt = cleanText(node.getAttribute('alt') || '');
        const src = node.getAttribute('src')?.trim();
        const title = cleanText(node.getAttribute('title') || '');

        if (src && (src.startsWith('data:') || src.includes('base64'))) {
          if (alt || title) {
            return `IMAGE: ${[alt, title].filter(Boolean).join(' - ')}`;
          }
          return null;
        }

        if (alt || src || title) {
          const parts = [alt, title].filter(Boolean);
          return `IMAGE: ${parts.join(' - ')}${src ? ` (${src})` : ''}`;
        }
      } catch (error) {
        reportIssue(error as Error, 'Error processing image element');
      }
      return null;
    }

    if (tagName === 'a') {
      try {
        const href = node.getAttribute('href')?.trim();
        const linkText = cleanText(node.textContent || '');
        if (linkText && href) {
          return `[${linkText}](${href})`;
        } else if (linkText) {
          return linkText;
        }
      } catch (error) {
        reportIssue(error as Error, 'Error processing link element');
      }
      return null;
    }

    if (CONVERT_TAGS.has(node.tagName)) {
      try {
        const meaningfulText = cleanText(node.textContent || '');
        const titleAttr = cleanText(node.getAttribute('title') || '');
        const altAttr = cleanText(node.getAttribute('alt') || '');
        const ariaLabel = cleanText(node.getAttribute('aria-label') || '');

        const contentParts = [
          meaningfulText,
          titleAttr,
          altAttr,
          ariaLabel,
        ].filter(Boolean);
        if (contentParts.length > 0) {
          return `${tagName.toUpperCase()}: ${contentParts.join(' ')}`;
        }
      } catch (error) {
        reportIssue(
          error as Error,
          `Error processing multimedia element: ${tagName}`
        );
      }
      return null;
    }

    if (tagName === 'input') {
      try {
        const type = node.getAttribute('type') || 'text';
        const placeholder = cleanText(node.getAttribute('placeholder') || '');
        const value = cleanText((node as HTMLInputElement).value || '');
        const label = cleanText(node.getAttribute('aria-label') || '');
        const parts = [label, placeholder, value].filter(Boolean);
        if (parts.length > 0) {
          return `Input (${type}): ${parts.join(' ')}`;
        }
      } catch (error) {
        reportIssue(error as Error, 'Error processing input element');
      }
      return null;
    }

    if (tagName === 'label') {
      try {
        const labelText = cleanText(node.textContent || '');
        if (labelText) {
          return `Label: ${labelText}`;
        }
      } catch (error) {
        reportIssue(error as Error, 'Error processing label element');
      }
      return null;
    }

    if (tagName === 'textarea') {
      try {
        const placeholder = cleanText(node.getAttribute('placeholder') || '');
        const value = cleanText((node as HTMLTextAreaElement).value || '');
        const label = cleanText(node.getAttribute('aria-label') || '');
        const parts = [label, placeholder, value].filter(Boolean);
        if (parts.length > 0) {
          return `Textarea: ${parts.join(' ')}`;
        }
      } catch (error) {
        reportIssue(error as Error, 'Error processing textarea element');
      }
      return null;
    }

    if (SEMANTIC_CONTAINERS.has(tagName)) {
      return null;
    }

    if (tagName === 'li') {
      try {
        const textContent = getDirectTextContent(node);
        if (textContent) {
          return `- ${textContent}`;
        }
      } catch (error) {
        reportIssue(error as Error, 'Error processing list item');
      }
      return null;
    }

    if (tagName === 'strong' || tagName === 'b') {
      try {
        const text = cleanText(node.textContent || '');
        if (text) {
          return `**${text}**`;
        }
      } catch (error) {
        reportIssue(error as Error, 'Error processing strong element');
      }
      return null;
    }

    if (tagName === 'em' || tagName === 'i') {
      try {
        const text = cleanText(node.textContent || '');
        if (text) {
          return `*${text}*`;
        }
      } catch (error) {
        reportIssue(error as Error, 'Error processing emphasis element');
      }
      return null;
    }

    try {
      if (node.children.length === 0) {
        let textContent = cleanText(node.textContent || '');
        textContent = textContent.replace(/\s+/g, ' ').trim();
        if (textContent) {
          return textContent;
        }
      }
    } catch (error) {
      reportIssue(error as Error, 'Error processing leaf text content');
    }

    return null;
  } catch (error) {
    reportIssue(error as Error, 'Error in extractSemanticContent');
    return null;
  }
};

const cleanText = (text: string): string => {
  try {
    if (!text) return '';

    let cleaned = text.trim();
    cleaned = Array.from(cleaned)
      .filter((char) => !DECORATIVE_CHARS.has(char))
      .join('');

    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    return cleaned ? cleaned : '';
  } catch (error) {
    reportIssue(error as Error, 'Error in cleanText');
    return (text || '').trim();
  }
};

const getDirectTextContent = (node: Element): string => {
  try {
    let text = '';

    if (!node || !node.childNodes) {
      return '';
    }

    for (const child of Array.from(node.childNodes)) {
      try {
        if (child.nodeType === TEXT_NODE_TYPE) {
          text += child.textContent || '';
        }
      } catch (error) {
        reportIssue(error as Error, 'Error processing child text node');
      }
    }

    text = cleanText(text);
    text = text.replace(/\s+/g, ' ').trim();

    return text;
  } catch (error) {
    reportIssue(error as Error, 'Error in getDirectTextContent');
    try {
      return cleanText(node.textContent || '');
    } catch (fallbackError) {
      reportIssue(
        fallbackError as Error,
        'Error in getDirectTextContent fallback'
      );
      return '';
    }
  }
};
